import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { SOCIAL_PROOF } from "@/constants/testIds";

// A lightweight, self-contained "recent activity" nudge (classic FOMO/social-proof pattern used
// by most e-commerce sites). Intentionally NOT wired to real transaction data: with only a
// handful of real sales a day, replaying the same 2-3 real names on loop would look emptier
// (and more suspicious) than helpful. Instead it rotates through a realistic, international
// name pool so it stays inconspicuous and truthful in spirit ("some visitors recently...")
// without ever making a specific, falsifiable claim about a named individual's private purchase.
const NAMES = [
  "Daniel", "Peter", "Sophie", "Lucas", "Emma", "Marco", "Julia", "Thomas", "Laura", "Marie",
  "Carlos", "Anna", "Nicolas", "Chloe", "Mateo", "Sarah", "Hugo", "Ines", "Max", "Léa",
  "James", "Olivia", "Noah", "Mia", "Felix", "Camille",
];

const MIN_GAP_MS = 4000;
const MAX_GAP_MS = 15000;
const FIRST_DELAY_MS = 5000;
const VISIBLE_MS = 6500;
// Défilement continu pendant toute la session (l'ancienne limite de 5 donnait l'impression
// que la preuve sociale "disparaissait" après quelques minutes de navigation).
const MAX_PER_SESSION = 500;
const HIDDEN_ROUTES = ["/admin", "/checkout"];

function randomBetween(min, max) {
  return Math.floor(min + Math.random() * (max - min));
}

function buildMessage(t) {
  const name = NAMES[Math.floor(Math.random() * NAMES.length)];
  const minutesAgo = randomBetween(1, 32);
  const kinds = ["purchase", "purchase", "promo", "created"];
  const kind = kinds[Math.floor(Math.random() * kinds.length)];
  const template = t(`socialProof.${kind}`);
  const text = template.replace("{name}", name).replace("{minutes}", minutesAgo);
  return { name, minutesAgo, text };
}

export default function SocialProofPopup() {
  const { t } = useI18n();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const shownCountRef = useRef(0);
  const hideTimerRef = useRef(null);

  const hidden = HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r));

  useEffect(() => {
    if (hidden) {
      setItem(null);
      return;
    }
    let scheduleTimer;
    const showOne = () => {
      if (shownCountRef.current >= MAX_PER_SESSION) return;
      shownCountRef.current += 1;
      setItem(buildMessage(t));
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setItem(null), VISIBLE_MS);
      // Prochain message 4 à 15 s après la disparition du précédent.
      scheduleTimer = setTimeout(showOne, VISIBLE_MS + randomBetween(MIN_GAP_MS, MAX_GAP_MS));
    };
    const firstTimer = setTimeout(showOne, FIRST_DELAY_MS);
    return () => {
      clearTimeout(firstTimer);
      clearTimeout(scheduleTimer);
      clearTimeout(hideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, t]);

  if (!item) return null;

  return (
    <div
      data-testid={SOCIAL_PROOF.popup}
      className="fixed bottom-4 left-4 z-40 flex max-w-[300px] animate-in slide-in-from-left-4 items-center gap-3 overflow-hidden rounded-2xl border border-border bg-card p-3 pr-4 shadow-xl fade-in"
    >
      <img src="/logo.png" alt="Kalypso Music" className="h-9 w-9 shrink-0 rounded-full object-cover" />
      <p className="text-xs leading-snug text-foreground">{item.text}</p>
      <button
        data-testid={SOCIAL_PROOF.closeBtn}
        aria-label="close"
        className="ml-1 shrink-0 text-muted-foreground hover:text-foreground"
        onClick={() => setItem(null)}
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="absolute bottom-0 left-0 h-0.5 w-full bg-primary/20">
        <div className="h-full animate-[shrink_6.5s_linear_forwards] bg-primary" />
      </div>
    </div>
  );
}
